import { ClarionButtonLink } from '@clarion/ClarionButtonLink';
import { Icon, IconProps } from '@clarion/ClarionIcon';
import { ClarionTag as Tag } from '@clarion/ClarionTag';
import { ReactNode } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useSideNavigation } from './SideNavigationContext';

type NavigationItemProps = {
  to: string;
  icon?: IconProps['icon'];
  children: ReactNode;
  onClick?: () => void;
  badge?: string | number; // For notifications/counts
  exact?: boolean;
};

export function NavigationItem({ to, icon, children, onClick, badge, exact }: NavigationItemProps) {
  const location = useLocation();
  const { location: pendingLocation } = useNavigation();
  const { isCollapsed } = useSideNavigation();

  const isActiveRoute = exact
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(to + '/');
  const isPendingRoute = exact
    ? pendingLocation?.pathname === to
    : pendingLocation?.pathname === to || pendingLocation?.pathname.startsWith(to + '/');

  const iconElement = icon && (
    <Icon icon={icon} className="text-gray-500 dark:text-gray-300 shrink-0" />
  );
  return (
    <ClarionButtonLink
      href={to}
      onClick={onClick}
      variant={isActiveRoute ? 'selected' : 'minimal'}
      intent={isActiveRoute ? 'primary' : 'none'}
      fill
      size={isCollapsed ? 'large' : 'medium'}
      textAlign="left"
      tooltip={isCollapsed ? children?.toString() : undefined}
      tooltipPlacement="right"
      className={isPendingRoute ? 'animation-pulse cursor-wait' : undefined}
      icon={isCollapsed ? icon : undefined}
    >
      {!isCollapsed && (
        <span className="flex items-center justify-between gap-2 w-full min-w-0 font-medium">
          {iconElement}
          <span className="truncate grow ml-2">{children}</span>
          {!!badge && !isCollapsed && (
            <Tag minimal intent="primary" className="float-end">
              {badge}
            </Tag>
          )}
        </span>
      )}
    </ClarionButtonLink>
  );
}
